export type PosDisplayPaymentStatus =
  | "idle"
  | "active"
  | "awaiting"
  | "card"
  | "tendered"
  | "saving"
  | "complete";

export type PosDisplayLineItem = {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  kind: "product" | "service";
};

export type PosDisplayState = {
  version: number;
  updatedAt: number;
  companyName: string;
  currencySymbol: string;
  customerName?: string | null;
  items: PosDisplayLineItem[];
  originalNet: number;
  discount: number;
  tax: number;
  total: number;
  paymentStatus: PosDisplayPaymentStatus;
  tenderLabel?: string | null;
  amountDue?: number | null;
  totalTendered?: number | null;
  changeDue?: number | null;
  amountPaid?: number | null;
};

export const POS_DISPLAY_CHANNEL = "glossa-pos-display";
export const POS_DISPLAY_STORAGE_KEY = "glossa_pos_display_state";
export const POS_DISPLAY_WINDOW_NAME = "glossa-customer-display";
export const POS_DISPLAY_BOUNDS_KEY = "glossa_pos_display_bounds";
export const POS_DISPLAY_ROUTE = "/pos/customer-display";

export const IDLE_POS_DISPLAY_STATE: PosDisplayState = {
  version: 1,
  updatedAt: 0,
  companyName: "",
  currencySymbol: "",
  customerName: null,
  items: [],
  originalNet: 0,
  discount: 0,
  tax: 0,
  total: 0,
  paymentStatus: "idle",
  tenderLabel: null,
  amountDue: null,
  totalTendered: null,
  changeDue: null,
  amountPaid: null,
};

type PosDisplayBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type ScreenDetailsLike = {
  screens: Array<{
    left: number;
    top: number;
    width: number;
    height: number;
    isPrimary?: boolean;
    availLeft?: number;
    availTop?: number;
    availWidth?: number;
    availHeight?: number;
  }>;
  currentScreen?: {
    isPrimary?: boolean;
    left: number;
    top: number;
  };
};

declare global {
  interface Window {
    getScreenDetails?: () => Promise<ScreenDetailsLike>;
  }
}

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    return null;
  }
  if (!channel) {
    channel = new BroadcastChannel(POS_DISPLAY_CHANNEL);
  }
  return channel;
}

export function isPosDualScreenEnabled(user: unknown): boolean {
  const val = (user as { company?: { pos_dual_screen?: unknown } })?.company?.pos_dual_screen;
  return val === true || val === 1 || val === "1" || val === "active";
}

export function readPosDisplayState(): PosDisplayState {
  try {
    const raw = localStorage.getItem(POS_DISPLAY_STORAGE_KEY);
    if (!raw) return { ...IDLE_POS_DISPLAY_STATE };
    const parsed = JSON.parse(raw) as PosDisplayState;
    return { ...IDLE_POS_DISPLAY_STATE, ...parsed };
  } catch {
    return { ...IDLE_POS_DISPLAY_STATE };
  }
}

export function patchPosDisplayState(partial: Partial<PosDisplayState>): PosDisplayState {
  const previous = readPosDisplayState();
  return publishPosDisplayState({
    ...previous,
    ...partial,
    companyName: partial.companyName ?? previous.companyName,
    currencySymbol: partial.currencySymbol ?? previous.currencySymbol,
  });
}

export function publishPosDisplayState(partial: Partial<PosDisplayState> & Pick<PosDisplayState, "companyName" | "currencySymbol">): PosDisplayState {
  const previous = readPosDisplayState();
  const next: PosDisplayState = {
    ...previous,
    ...partial,
    version: 1,
    updatedAt: Date.now(),
    items: partial.items ?? previous.items ?? [],
  };

  try {
    localStorage.setItem(POS_DISPLAY_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore quota errors
  }

  getChannel()?.postMessage({ type: "state", payload: next });
  return next;
}

export function clearPosDisplayState(branding?: {
  companyName?: string;
  currencySymbol?: string;
}): PosDisplayState {
  return publishPosDisplayState({
    ...IDLE_POS_DISPLAY_STATE,
    companyName: branding?.companyName ?? "",
    currencySymbol: branding?.currencySymbol ?? "",
    paymentStatus: "idle",
  });
}

export function subscribePosDisplayState(
  onState: (state: PosDisplayState) => void,
): () => void {
  const handleMessage = (event: MessageEvent) => {
    if (event?.data?.type === "state" && event.data.payload) {
      onState(event.data.payload as PosDisplayState);
    }
    if (event?.data?.type === "close") {
      // no-op for subscribers; window manager handles close
    }
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== POS_DISPLAY_STORAGE_KEY || !event.newValue) return;
    try {
      onState(JSON.parse(event.newValue) as PosDisplayState);
    } catch {
      // ignore
    }
  };

  const ch = getChannel();
  ch?.addEventListener("message", handleMessage);
  window.addEventListener("storage", handleStorage);

  // Initial hydrate
  onState(readPosDisplayState());

  return () => {
    ch?.removeEventListener("message", handleMessage);
    window.removeEventListener("storage", handleStorage);
  };
}

function readSavedBounds(): PosDisplayBounds | null {
  try {
    const raw = localStorage.getItem(POS_DISPLAY_BOUNDS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PosDisplayBounds;
  } catch {
    return null;
  }
}

export function rememberPosDisplayBounds(win: Window): void {
  try {
    const bounds: PosDisplayBounds = {
      left: win.screenX,
      top: win.screenY,
      width: win.outerWidth,
      height: Math.max(win.outerHeight, 600),
    };
    localStorage.setItem(POS_DISPLAY_BOUNDS_KEY, JSON.stringify(bounds));
  } catch {
    // ignore
  }
}

async function resolveSecondaryScreenBounds(): Promise<PosDisplayBounds | null> {
  const saved = readSavedBounds();
  if (saved && saved.width > 200 && saved.height > 200) {
    return saved;
  }

  try {
    if (typeof window.getScreenDetails !== "function") {
      return null;
    }
    const details = await window.getScreenDetails();
    const screens = details?.screens || [];
    if (screens.length < 2) return null;

    const secondary =
      screens.find((s) => !s.isPrimary) ||
      screens.find(
        (s) =>
          s.left !== details.currentScreen?.left ||
          s.top !== details.currentScreen?.top,
      ) ||
      screens[1];

    if (!secondary) return null;

    return {
      left: secondary.availLeft ?? secondary.left,
      top: secondary.availTop ?? secondary.top,
      width: secondary.availWidth ?? secondary.width,
      height: secondary.availHeight ?? secondary.height,
    };
  } catch {
    return null;
  }
}

function buildFeatures(bounds: PosDisplayBounds | null): string {
  if (!bounds) {
    return "width=1280,height=800";
  }
  return [
    `left=${Math.round(bounds.left)}`,
    `top=${Math.round(bounds.top)}`,
    `width=${Math.round(bounds.width)}`,
    `height=${Math.round(bounds.height)}`,
  ].join(",");
}

let displayWindowRef: Window | null = null;

export function getPosDisplayWindow(): Window | null {
  if (displayWindowRef && !displayWindowRef.closed) {
    return displayWindowRef;
  }
  return null;
}

export async function openPosCustomerDisplay(options?: {
  force?: boolean;
}): Promise<{ opened: boolean; blocked: boolean; window: Window | null }> {
  const existing = getPosDisplayWindow();
  if (existing && !options?.force) {
    existing.focus();
    return { opened: true, blocked: false, window: existing };
  }

  const bounds = await resolveSecondaryScreenBounds();
  const url = `${window.location.origin}${POS_DISPLAY_ROUTE}`;
  const win = window.open(url, POS_DISPLAY_WINDOW_NAME, buildFeatures(bounds));

  if (!win) {
    return { opened: false, blocked: true, window: null };
  }

  displayWindowRef = win;

  // Persist bounds after a short delay (window may animate into place)
  window.setTimeout(() => {
    if (displayWindowRef && !displayWindowRef.closed) {
      rememberPosDisplayBounds(displayWindowRef);
    }
  }, 800);

  return { opened: true, blocked: false, window: win };
}

export function closePosCustomerDisplay(): void {
  getChannel()?.postMessage({ type: "close" });
  const win = getPosDisplayWindow();
  if (win && !win.closed) {
    try {
      rememberPosDisplayBounds(win);
      win.close();
    } catch {
      // ignore
    }
  }
  displayWindowRef = null;
}

export function isPosDisplayWindowOpen(): boolean {
  const win = getPosDisplayWindow();
  return !!(win && !win.closed);
}
